import CloseIcon from "@mui/icons-material/Close";
import {Box, Dialog, IconButton, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import SigningQrCode from "./SigningQrCode";

interface Props {
    open: boolean
    onClose: () => void
    value: string
    logoUrl?: string
}

const QR_SIZE = 'min(70vw, 70vh)'

/** Poster-sized, not hero-sized: the code stays the thing to look at. */
const LOGO_MAX_HEIGHT = 72

/** Comfortably past the 48px touch guideline. */
const CORNER_BUTTON_SIZE = 56

/**
 * Copied from `surveys/share/SurveyQrFullscreenDialog.tsx`: the code as large as the screen allows,
 * so the spa tablet's camera picks it up from the staff screen. Nothing else is on it - no guest
 * data and no link text - and there is no PNG download: the link is only valid for minutes. Staff
 * leave with the corner button or Escape; the backdrop is inert so a stray touch doesn't close it.
 * It closes by itself once the link is no longer live, because the digital signing dialog stops
 * rendering it.
 */
const SigningQrHandover = ({open, onClose, value, logoUrl}: Props) => {

    const {t} = useTranslation()

    return (
        <Dialog
            open={open}
            fullScreen
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return
                onClose()
            }}
            PaperProps={{sx: {backgroundColor: 'background.default'}}}
        >
            <IconButton
                aria-label={t('Close')}
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 1,
                    width: CORNER_BUTTON_SIZE,
                    height: CORNER_BUTTON_SIZE,
                    color: 'text.secondary'
                }}
            >
                <CloseIcon fontSize={'large'}/>
            </IconButton>

            <Stack
                alignItems={'center'}
                justifyContent={'center'}
                gap={3}
                sx={{flex: 1, p: 4, overflow: 'auto'}}
            >
                {!!logoUrl && (
                    <Box
                        component={'img'}
                        src={logoUrl}
                        alt={''}
                        sx={{
                            display: 'block',
                            maxHeight: LOGO_MAX_HEIGHT,
                            maxWidth: 'min(60%, 320px)',
                            objectFit: 'contain',
                        }}
                    />
                )}

                <Typography variant={'h4'} fontWeight={600} textAlign={'center'} sx={{maxWidth: '90%'}}>
                    {t('Digital signature')}
                </Typography>

                <SigningQrCode value={value} size={QR_SIZE} padding={3} logoUrl={logoUrl}/>

                <Typography variant={'body1'} color={'text.secondary'} textAlign={'center'}>
                    {t("Scan the code with the tablet's camera")}
                </Typography>
            </Stack>
        </Dialog>
    )
}

export default SigningQrHandover
